import { db, studioDb } from "../../firebase";
import { doc, getDoc, onSnapshot } from "firebase/firestore";

const TV_BUSINESS_CONTEXT_KEY = "tv.businessContext.v1";

const normalizeId = (value) => String(value || "").trim() || null;

function readPersistedContext() {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(TV_BUSINESS_CONTEXT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const uid = normalizeId(parsed?.uid);
    return uid ? { uid, businessId: normalizeId(parsed?.businessId) } : null;
  } catch {
    return null;
  }
}

function writePersistedContext({ uid, businessId }) {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(
      TV_BUSINESS_CONTEXT_KEY,
      JSON.stringify({
        uid: normalizeId(uid),
        businessId: normalizeId(businessId),
        resolvedAt: Date.now(),
      })
    );
  } catch {
    // best-effort cache only
  }
}

export function clearTvSessionBusinessContext() {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.removeItem(TV_BUSINESS_CONTEXT_KEY);
  } catch {
    // no-op
  }
}

/**
 * One-time read for users/{uid}.primaryBusinessId.
 * If already resolved for this uid, reuse the cached value for the session.
 */
export async function resolveTvSessionBusinessId(uid, { forceRefresh = false } = {}) {
  const normalizedUid = normalizeId(uid);
  if (!normalizedUid) return null;

  if (!forceRefresh) {
    const cached = readPersistedContext();
    if (cached?.uid === normalizedUid) return cached.businessId || null;
  }

  const userSnap = await getDoc(doc(db, "users", normalizedUid));
  const nextBusinessId = userSnap.exists()
    ? normalizeId(userSnap.data()?.primaryBusinessId)
    : null;

  writePersistedContext({ uid: normalizedUid, businessId: nextBusinessId });
  return nextBusinessId;
}

/**
 * TV contract:
 * - Never subscribe to users/{uid}
 * - Resolve primaryBusinessId once (users/{uid}) and freeze for session
 * - Live subscribe only to businesses/{sessionBusinessId} then collections/{activeCollectionId}
 * - Recreate collection listener only when activeCollectionId changes
 */
export function subscribeTvBusinessCollectionContext({ uid } = {}, onNext, onError) {
  const normalizedUid = normalizeId(uid);

  let disposed = false;
  let unsubBusiness = null;
  let unsubCollection = null;
  let sessionBusinessId = null;
  let activeCollectionId = null;

  const emit = (patch = {}) => {
    onNext?.({
      uid: normalizedUid,
      sessionBusinessId,
      activeCollectionId,
      items: [],
      itemsMeta: {},
      updatedAt: null,
      ...patch,
    });
  };

  const unsubscribeCollection = () => {
    if (typeof unsubCollection === "function") unsubCollection();
    unsubCollection = null;
  };

  const subscribeCollection = (nextCollectionId) => {
    const normalizedCollectionId = normalizeId(nextCollectionId);
    if (normalizedCollectionId === activeCollectionId) return;

    unsubscribeCollection();
    activeCollectionId = normalizedCollectionId;

    if (!activeCollectionId) {
      emit({ status: "no_active_collection" });
      return;
    }

    unsubCollection = onSnapshot(
      doc(studioDb, "collections", activeCollectionId),
      (collectionSnap) => {
        if (!collectionSnap.exists()) {
          emit({
            status: "collection_unavailable",
            collectionExists: false,
          });
          return;
        }

        const collectionData = collectionSnap.data() || {};
        const items = Array.isArray(collectionData?.items)
          ? collectionData.items
          : Array.isArray(collectionData?.itemIds)
            ? collectionData.itemIds
            : [];
        const itemsMeta =
          collectionData?.itemsMeta && typeof collectionData.itemsMeta === "object"
            ? collectionData.itemsMeta
            : {};

        const renderItems = items.map((itemId) => ({
          id: itemId,
          meta: { ...(itemsMeta[itemId] || {}) },
        }));

        emit({
          status: "ready",
          collectionExists: true,
          items,
          itemsMeta,
          renderItems,
          updatedAt: collectionData?.updatedAt || null,
        });
      },
      (error) => {
        emit({
          status: "collection_unavailable",
          collectionExists: false,
          error,
        });
        onError?.(error);
      }
    );
  };

  const start = async () => {
    if (!normalizedUid) {
      emit({ status: "signed_out" });
      return;
    }

    try {
      sessionBusinessId = await resolveTvSessionBusinessId(normalizedUid);
    } catch (error) {
      emit({ status: "no_active_business", error });
      onError?.(error);
      return;
    }

    if (disposed) return;

    if (!sessionBusinessId) {
      emit({ status: "no_active_business" });
      return;
    }

    unsubBusiness = onSnapshot(
      doc(studioDb, "businesses", sessionBusinessId),
      (businessSnap) => {
        if (!businessSnap.exists()) {
          unsubscribeCollection();
          activeCollectionId = null;
          emit({ status: "business_unavailable" });
          return;
        }

        const businessData = businessSnap.data() || {};
        subscribeCollection(businessData?.activeCollectionId);
      },
      (error) => {
        unsubscribeCollection();
        activeCollectionId = null;
        emit({ status: "business_unavailable", error });
        onError?.(error);
      }
    );
  };

  start();

  return () => {
    disposed = true;
    unsubscribeCollection();
    if (typeof unsubBusiness === "function") unsubBusiness();
  };
}

